

































































































import { Collapse, Event, EventItem, Ticket } from '@openticket/lib-shop';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { getSelectorClass } from '@/utils';
import DatePicker from '../../components/DatePicker.vue';
import {
    createFilters,
    TimeslotFilter,
    TimeslotFilters,
    toTimeRangeString,
} from './timeslots';

@Component({
    components: {
        DatePicker,
    },
})
export default class Timeslots extends Vue {
    loading = false;

    timeslotFilters: TimeslotFilters = createFilters();
    ticketCounts: { [key: string]: number } = {};

    couponListener = '';

    created(): void {
        const { timeslot } = this.$route.query;

        this.timeslotFilters.update(
            this.$shop.data,
            timeslot && typeof timeslot === 'string' ? timeslot : null
        );

        this.$emit('filter-updated', this.timeslotFilters.selected);

        this.calculateTicketCount();

        // This is a fix for a specific problem (hide without coupon).
        // However, once updates on models are handled a bit more generalized,
        // this can also be handled better.
        this.couponListener = this.$shop.cart.on(['coupon'], () => {
            this.calculateTicketCount();
        });
    }

    destroyed(): void {
        this.$shop.cart.off(this.couponListener);
    }

    async onDateChange(start: string): Promise<void> {
        // Parsing and then formatting the end date are done a bit complex.
        // This is due to the date constructor considering a date string (2023-01-01) as UTC,
        // where a string containing time is evaluated as the user's local timezone...
        // https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date/Date#sect2
        if (!/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/.test(start)) {
            throw new Error('Invalid start date');
        }

        const [y, m, d] = start.split('-').map((v: string) => parseInt(v, 10));
        const endDate = new Date(y, m - 1, d + 1);
        const end: string = [
            endDate.getFullYear(),
            endDate.getMonth() + 1,
            endDate.getDate(),
        ]
            .map((v: number) => v.toFixed(0).padStart(2, '0'))
            .join('-');

        this.loading = true;

        await this.selectTimeslot();

        await this.$shop.updateData({ start, end });

        this.timeslotFilters.update(this.$shop.data);
        this.calculateTicketCount();

        this.$emit('filter-updated', this.timeslotFilters.selected);

        this.loading = false;
    }

    get show(): boolean {
        return (
            (!this.$settings ||
                !this.$settings.static ||
                !!this.$settings.static.shop.enableTimeslotsFilters) &&
            !this.loading
        );
    }

    get enabledDates(): any {
        return this.$shop.data.available_future_dates_nested;
    }

    get currentDate(): string {
        return this.$shop.data.filters.start;
    }

    private getSelectorClass(key: string): string {
        return getSelectorClass('timeslot', key);
    }

    calculateTicketCount(): void {
        this.ticketCounts = {};

        for (const event of this.$shop.data.events) {
            for (const item of event.items) {
                if (eventItemIsCollapse(item)) {
                    for (const ticket of item.item.tickets) {
                        this.calculateIndividualTicketAvailable(event, ticket);
                    }
                } else if (eventItemIsTicket(item)) {
                    this.calculateIndividualTicketAvailable(event, item.item);
                }
            }
        }
    }

    calculateIndividualTicketAvailable(event: Event, ticket: Ticket): void {
        if (
            !['available', 'not_sold_right_now'].includes(ticket.status) ||
            ticket.hidden
        ) {
            return;
        }

        let hit = 0;

        for (const product of ticket.products.required) {
            // @ts-expect-error - This is property is not in the type, it should be.
            // However, as this is only used for comparison, not updating the library only for this.
            if (product.item.origin_type === 'date') {
                hit++;

                for (const eventDate of product.item.event_dates) {
                    const key = toTimeRangeString(eventDate);
                    this.$set(
                        this.ticketCounts,
                        key,
                        (this.ticketCounts[key] || 0) + 1
                    );
                }
            }
        }

        if (hit) {
            return;
        }

        const { start, end } = event;

        if (!start || !end) {
            return;
        }

        const key = toTimeRangeString({ start, end });

        this.$set(this.ticketCounts, key, (this.ticketCounts[key] || 0) + 1);
    }

    async selectTimeslot(filter?: TimeslotFilter): Promise<void> {
        if (filter && !this.ticketCounts[filter.key]) {
            // Ignores and keeps the current selection untouched
            return;
        }

        const query: {
            [key: string]: string | (string | null)[] | null | undefined;
        } = {
            ...this.$route.query,
        };

        if (filter) {
            query.timeslot = filter.key;
        } else {
            delete query.timeslot;
        }

        if (this.$route.query.timeslot === query.timeslot) {
            // Skip if the current route is already correct, but silently ensure the selected filter is in sync.
            this.timeslotFilters.selected = filter || null;
            return;
        }

        this.timeslotFilters.selected = filter || null;

        this.$emit('filter-updated', this.timeslotFilters.selected);

        await this.$router.push({
            name: this.$route.name as string,
            params: this.$route.params,
            query,
        });
    }

    @Watch('loading')
    onLoadingChange(): void {
        this.$emit('loading', this.loading);
    }
}

function eventItemIsCollapse(
    item: EventItem
): item is EventItem & { item: Collapse } {
    return item.type === 'collapse';
}

function eventItemIsTicket(
    item: EventItem
): item is EventItem & { item: Ticket } {
    return item.type === 'ticket';
}
